import flatten from 'lodash/flatten'
import { colors } from 'ic-snacks'

import { type ChartSeries, type ChartDomain } from 'legacy/components/DataViz/dataViz.types'
import { type FormatNumberOptions, type IntlShape } from 'react-intl'

export const NO_DATA_LABEL = 'NO_DATA_LABEL'

const seriesColors: { [Key in ChartSeries['type']]: string } = {
  primary: colors.GREEN_500,
  secondary: colors.GRAY_46,
  this_period: colors.GREEN_500,
  last_period: colors.GRAY_46,
  GOOD: colors.GREEN_500,
  OK: '#ff7a00',
  BAD: colors.RED_500,
}

export function getSeriesColor(type: ChartSeries['type']): string {
  return seriesColors[type]
}

const seriesAreaColors: { [Key in ChartSeries['type']]: string } = {
  primary: colors.GREEN_400,
  secondary: colors.GRAY_46,
  this_period: colors.GREEN_400,
  last_period: colors.GRAY_13,
  GOOD: colors.GREEN_400,
  OK: '#ff7a00',
  BAD: colors.RED_400,
}

export function getSeriesAreaColor(type: ChartSeries['type']): string {
  return seriesAreaColors[type]
}

export function getFormattedTime(intl: IntlShape, time: number, isDay: boolean) {
  const date = new Date(time)

  return isDay
    ? intl.formatTime(date, { hour: '2-digit' })
    : intl.formatDate(date, {
        month: 'numeric',
        year: 'numeric',
        day: 'numeric',
      })
}

const labelFormatOptions: {
  [key: string]: FormatNumberOptions
} = {
  percent: { style: 'percent' },
  currency: {
    style: 'currency',
    currency: 'USD',
  },
}

// eslint-disable-next-line max-params
export function getFormattedLabel(
  intl: IntlShape,
  dataType: 'currency' | 'number' | 'percent',
  isDay: boolean,
  value: number | null,
  time: number
) {
  const valueOptions = labelFormatOptions[dataType]
  const formattedValue =
    value === null
      ? intl.formatMessage({ id: 'charts.tooltip.noData' })
      : intl.formatNumber(value, valueOptions)
  const formattedTime = getFormattedTime(intl, time, isDay)

  return isDay
    ? intl.formatMessage(
        { id: 'dashboard.charts.labelHour' },
        { value: formattedValue, hour: formattedTime }
      )
    : intl.formatMessage(
        { id: 'dashboard.charts.labelDay' },
        { value: formattedValue, date: formattedTime }
      )
}

const yTickFormatOptions: {
  [key: string]: FormatNumberOptions
} = {
  percent: { style: 'percent' },
  currency: {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
}

// eslint-disable-next-line max-params
export function formatYTick(
  intl: IntlShape,
  dataType: 'currency' | 'number' | 'percent',
  domainStart: number,
  value: number,
  index: number
) {
  const options = yTickFormatOptions[dataType]

  if (dataType === 'percent') {
    return intl.formatNumber(value, options)
  }

  return value > domainStart && index % 2 === 0 ? `${intl.formatNumber(value, options)}` : ''
}

const minMax = (value: number | null, domainRange: [number, number]): [number, number] => {
  if (value === null) {
    return domainRange
  }

  const start = value < domainRange[0] ? value : domainRange[0]
  const end = value > domainRange[1] ? value : domainRange[1]

  return [start, end]
}

export const generateDomain = (
  data: { x: number; y: number | null }[][],
  dataType?: 'currency' | 'number' | 'percent'
): ChartDomain => {
  const defaultDomain: ChartDomain = {
    x: [Number.MAX_SAFE_INTEGER, 0],
    y: [Number.MAX_SAFE_INTEGER, 0],
  }

  const domain = flatten(data).reduce<ChartDomain>(
    (result, datum) => ({
      x: minMax(datum.x, result.x),
      y: minMax(datum.y, result.y),
    }),
    defaultDomain
  )

  // Victory does not like 0 length domains,
  // so arbitrarily setting them to 10
  if (domain.y[0] === domain.y[1]) {
    domain.y[1] = domain.y[0] + 10
  }

  // If this happens, all data is null
  if (domain.y[0] === Number.MAX_SAFE_INTEGER) {
    domain.y[0] = 0
    if (dataType === 'currency') {
      domain.y[1] = 10
    } else if (dataType === 'percent') {
      domain.y[1] = 1
    }
  }

  if (dataType === 'percent') {
    // Ensure the Y axis labels cover the max (round up to nearest 5% increment)
    domain.y[1] += 0.05 - (domain.y[1] % 0.05)

    // Give a little padding on the high end if 100% is not the max
    if (domain.y[1] !== 1) {
      domain.y[1] = Math.min(domain.y[1] + 0.05, 1)
    }

    // Give it some padding on the low end if 0% is not the min
    if (domain.y[0] !== 0) {
      const paddedYMin = domain.y[0] - (domain.y[1] - domain.y[0]) * 0.5
      domain.y[0] = Math.max(0, paddedYMin)
    }
  }

  return domain
}
